import DocumentMeta from 'core/components/DocumentMeta'
import DataKeys from 'k8s/DataKeys'
import React from 'react'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import useListAction from 'core/hooks/useListAction'
import { pick } from 'ramda'
import { routes } from 'core/utils/routes'
import { listReplicaSets, deleteReplicaSet } from './actions'
import { createResourceLabelsCell } from '../common/entity/labels-and-annotations/helpers'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { ArrayElement } from 'core/actions/Action'
import { replicaSetSelector } from 'k8s/components/replica-sets/selectors'
import AddWorkloadResourcePage from 'k8s/components/common/entity/AddResourcePage'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import InferActionParams from 'core/actions/InferActionParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { useAppSelector } from 'app/store'
import { durationBetweenDates } from 'utils/misc'

type ModelDataKey = DataKeys.ReplicaSets
type SelectorModel = ArrayElement<ReturnType<typeof replicaSetSelector>>
type ActionParams = InferActionParams<typeof listReplicaSets>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {
  namespace?: string
  masterNodeClusters: boolean
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId']
const defaultParams: Params = {
  clusterId: null,
  masterNodeClusters: true,
  healthyClusters: true,
}
const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ReplicaSets',
  listTablePrefs,
)

const searchTargets = ['name', 'clusterName']
const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ clusterId, id }) => routes.replicaSets.details.path({ clusterId, id }),
    }),
  },
  { key: 'namespace', label: 'Namespace' },
  { key: 'clusterName', label: 'Cluster', width: 'medium' },
  {
    key: 'creationTimestamp',
    label: 'Age',
    formatFn: (value) => durationBetweenDates({ labels: ['d'] })(value),
  },
  { key: 'replicas.desired', label: 'Desired', tooltip: 'The number of desired replicas' },
  {
    key: 'replicas.available',
    label: 'Available',
    tooltip: 'The number of available replicas for this replica set',
  },
  {
    key: 'replicas.ready',
    label: 'Ready',
    tooltip: 'The number of pods targeted by this ReplicaSet with a Ready condition',
  },
  {
    key: 'selector',
    label: 'Selectors',
    CellComponent: createResourceLabelsCell({ type: 'table', separator: '=', variant: 'default' }),
  },
  {
    key: 'labels',
    label: 'Labels',
    CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
  },
  {
    key: 'annotations',
    label: 'Annotations',
    CellComponent: createResourceLabelsCell({ type: 'table', separator: ': ' }),
  },
]

export default function ReplicaSetsListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listReplicaSets, {
    params,
    requiredParams,
  })
  const data = useAppSelector(replicaSetSelector)
  return (
    <>
      <DocumentMeta title="Replica Sets" />
      <AddWorkloadResourcePage resourceType="replicaSet" addRoute={routes.replicaSets.add} />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.ReplicaSets}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={data}
        columns={columns}
        addUrl={routes.replicaSets.add.path()}
        addText="Add Replica Set"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteReplicaSet}
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
